/**
 * Centralized API error handling utilities
 */

import { apiFetch } from '@/lib/api-client';
import {
  createInterviewError,
  type InterviewError,
  InterviewErrorCode,
} from '@/lib/errors/interview-errors';
import { logger } from '@/utils/logger';

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: InterviewErrorCode;
  status?: number;
  details?: unknown;
}

/**
 * Options for apiClient, based on fetch RequestInit
 */
export interface ApiClientOptions {
  method?: string;
  body?: string | null;
  headers?: Record<string, string>;
  credentials?: 'omit' | 'same-origin' | 'include';
  mode?: 'cors' | 'no-cors' | 'same-origin';
  cache?: 'default' | 'no-store' | 'reload' | 'no-cache' | 'force-cache';
  redirect?: 'follow' | 'error' | 'manual';
  // eslint-disable-next-line no-undef
  signal?: AbortSignal | null;
}

/**
 * Maps HTTP status codes to user-friendly error messages and codes
 */
export function mapHttpStatusToError(
  status: number,
  responseData?: ApiErrorResponse,
): InterviewError {
  // Check if response already has a typed error
  if (responseData?.code) {
    return createInterviewError(
      responseData.code,
      responseData.message || responseData.error || 'An error occurred',
      responseData.details,
    );
  }

  // Map common HTTP status codes
  switch (status) {
    case 400:
      return createInterviewError(
        InterviewErrorCode.INTERNAL_ERROR,
        'Invalid request. Please check your input and try again.',
      );

    case 401:
      return createInterviewError(
        InterviewErrorCode.USER_NOT_AUTHENTICATED,
        'Please log in to continue.',
      );

    case 403:
      return createInterviewError(
        InterviewErrorCode.UNAUTHORIZED,
        responseData?.error === 'Interview limit reached'
          ? 'You have reached your interview limit.'
          : 'You are not authorized to perform this action.',
      );

    case 404:
      return createInterviewError(
        InterviewErrorCode.SESSION_NOT_FOUND,
        'The requested resource was not found.',
      );

    case 429:
      return createInterviewError(
        InterviewErrorCode.NETWORK_ERROR,
        'Too many requests. Please wait a moment and try again.',
      );

    case 500:
    case 502:
    case 503:
    case 504:
      return createInterviewError(
        InterviewErrorCode.INTERNAL_ERROR,
        'Something went wrong. Please try again later.',
      );

    default:
      return createInterviewError(
        InterviewErrorCode.UNKNOWN_ERROR,
        `An unexpected error occurred (${status})`,
      );
  }
}

/**
 * Handle fetch errors and responses uniformly
 */
export async function handleApiResponse<T = unknown>(
  response: Response,
): Promise<T> {
  let responseData: ApiErrorResponse | undefined;

  // Try to parse response body
  try {
    const text = await response.text();
    if (text) {
      responseData = JSON.parse(text);
    }
  } catch (err) {
    logger.debug('Failed to parse response body', {
      status: response.status,
      error: err,
    });
  }

  // Check if response is OK
  if (!response.ok) {
    const error = mapHttpStatusToError(response.status, responseData);
    logger.error('API request failed', {
      status: response.status,
      url: response.url,
      error: error,
    });
    throw error;
  }

  // Return parsed data for successful responses
  return responseData as T;
}

/**
 * Wrapper for fetch with built-in error handling
 */
export async function apiClient<T = unknown>(
  url: string,
  options?: ApiClientOptions,
): Promise<T> {
  try {
    // apiFetch attaches the current Supabase access token as a Bearer
    // header so the Hono auth middleware can identify the user.
    const response = await apiFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    return handleApiResponse<T>(response);
  } catch (err) {
    // Handle network errors
    if (err instanceof TypeError && err.message.includes('fetch')) {
      const networkError = createInterviewError(
        InterviewErrorCode.NETWORK_ERROR,
        'Network error. Please check your connection and try again.',
      );
      logger.error('Network error during API call', {
        url,
        error: err,
      });
      throw networkError;
    }

    // Re-throw if it's already an InterviewError
    if (err && typeof err === 'object' && 'code' in err) {
      throw err;
    }

    // Handle other errors
    const unknownError = createInterviewError(
      InterviewErrorCode.UNKNOWN_ERROR,
      err instanceof Error ? err.message : 'An unexpected error occurred',
    );
    logger.error('Unexpected error during API call', {
      url,
      error: err,
    });
    throw unknownError;
  }
}

/**
 * Helper to extract error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }

  if (error && typeof error === 'object' && 'error' in error) {
    return (error as { error: string }).error;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}
